import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { CallPageView } from "./CallViews";
import { getSharedCallMedia, getSharedCallPage, SharedLinkGone } from "./sharedApi";
import type { SharedCallPageData } from "./sharedApi";
import "./calls.css";

/** Keep the link out of search results and out of any Referer header while this page is open. */
function useLinkPrivacyMeta() {
  useEffect(() => {
    const added: HTMLMetaElement[] = [];
    for (const [name, content] of [
      ["robots", "noindex"],
      ["referrer", "no-referrer"],
    ]) {
      const meta = document.createElement("meta");
      meta.name = name;
      meta.content = content;
      document.head.appendChild(meta);
      added.push(meta);
    }
    return () => added.forEach((meta) => meta.remove());
  }, []);
}

function seek(seconds: number) {
  const media = document.querySelector<HTMLMediaElement>(".dg-call-stage video, .dg-call-audio");
  if (!media) return;
  const resume = !media.paused;
  const length = media.duration;
  media.currentTime = Number.isFinite(length) ? Math.min(seconds, length) : seconds;
  if (resume) media.play().catch(() => undefined);
}

/** A call opened from a share link: the owner's page, read-only, no sign-in. */
export default function SharedCallPage() {
  const { token = "" } = useParams();
  const [page, setPage] = useState<SharedCallPageData | null>(null);
  const [error, setError] = useState<string | null>(null);
  useLinkPrivacyMeta();

  const load = () => {
    setError(null);
    getSharedCallPage(token)
      .then(setPage)
      .catch((reason: unknown) =>
        setError(
          reason instanceof SharedLinkGone || reason instanceof Error
            ? reason.message
            : "Could not load this call.",
        ),
      );
  };

  useEffect(() => {
    load();
    // Reloading when the token changes is the whole effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (error) return <p className="dg-call-note">{error}</p>;
  if (!page) return <p className="dg-call-note">Loading…</p>;

  const expires = new Date(page.shared.expires_at);
  return (
    <div className="dg-shared-call">
      <CallPageView
        page={page}
        readOnly
        onSeek={seek}
        onRefresh={load}
        onRefreshMedia={async () => {
          const media = await getSharedCallMedia(token);
          setPage((current) => (current ? { ...current, media } : current));
          return media;
        }}
        banner={
          Number.isNaN(expires.getTime()) ? null : (
            <p className="dg-call-note">Shared until {expires.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}</p>
          )
        }
      />
    </div>
  );
}
